<template>
  <v-card class="mt-4">
    <v-card-title>Deine gespeicherten Daten</v-card-title>
    <v-card-text>
      {{ kontext === "club" ? "Hier bekommst du alles, was schuetzenbuch.de zu diesem Vereinskonto speichert" : "Hier bekommst du alles, was schuetzenbuch.de zu deinem Konto speichert" }}
      – als PDF zum Lesen oder als JSON-Datei zur Weiterverarbeitung (Auskunft nach Art. 15 DSGVO).
      Jeder Abruf wird protokolliert und erscheint in der nächsten Auskunft.
    </v-card-text>
    <v-card-actions class="px-4">
      <div class="d-flex flex-wrap align-center">
        <v-btn
            color="primary"
            outlined
            class="mr-2 mb-2"
            :loading="laedt === 'pdf'"
            :disabled="!!laedt"
            @click="pdfErstellen">
          Meine Daten (DSGVO-Auskunft)
          <v-icon right>mdi-file-pdf-outline</v-icon>
        </v-btn>
        <v-btn
            text
            small
            color="primary"
            class="mb-2"
            :loading="laedt === 'json'"
            :disabled="!!laedt"
            @click="jsonHerunterladen">
          als JSON
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import swal from "sweetalert2";
import { API } from "@/plugins/axios";

// Auskunft nach Art. 15 DSGVO: PDF aus ?format=lesefassung, JSON (Art. 15 Abs. 3) ohne Parameter.
// Aufbau des PDFs wie die Filament-Vorlage im Backend (resources/views/datenauskunft/auskunft.blade.php).
export default {
  name: "DatenauskunftExport",
  props: {
    // "user" oder "club"
    kontext: {
      type: String,
      required: true
    }
  },
  data: () => ({
    laedt: null
  }),
  methods: {
    async abrufen(format) {
      const token = this.kontext === "club"
        ? this.$store.state.ClubAuth.Clubtoken
        : this.$store.state.UserAuth.token;
      const resp = await API.get(`${this.kontext}/datenauskunft`, {
        params: format ? { format } : {},
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      return resp.data;
    },
    fehler(error) {
      // Netzwerkfehler und 401 behandelt schon der Axios-Interceptor
      if (!error.response || [401, 419].includes(error.response.status)) {
        return;
      }
      swal.fire(
        "Auskunft nicht möglich",
        "Deine Daten konnten gerade nicht zusammengestellt werden. Versuch es bitte später noch einmal.",
        "error"
      );
    },
    dateiname(daten, endung) {
      const heute = new Date().toISOString().slice(0, 10);
      const name = (daten.auskunft.betroffen.bezeichnung || "").replace(/[\\/:*?"<>|]/g, "").trim();
      return `Datenauskunft ${name} ${heute}.${endung}`;
    },
    async jsonHerunterladen() {
      this.laedt = "json";
      try {
        const daten = await this.abrufen(null);
        const blob = new Blob([JSON.stringify(daten, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = this.dateiname(daten, "json");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.fehler(error);
      } finally {
        this.laedt = null;
      }
    },
    async pdfErstellen() {
      this.laedt = "pdf";
      try {
        const daten = await this.abrufen("lesefassung");
        this.pdfBauen(daten).save(this.dateiname(daten, "pdf"));
      } catch (error) {
        this.fehler(error);
      } finally {
        this.laedt = null;
      }
    },
    zeitpunkt(iso) {
      const d = new Date(iso);
      if (isNaN(d)) {
        return iso || "–";
      }
      const zwei = n => String(n).padStart(2, "0");
      return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()} ${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
    },
    pdfBauen(daten) {
      const kopf = daten.auskunft;
      const info = daten.informationen;
      const v = kopf.verantwortlicher;
      const b = info.beschwerderecht;
      const erstellt = this.zeitpunkt(kopf.erstellt_am);

      const doc = new jsPDF({ orientation: "landscape", format: "a4" });
      const rand = 12;
      const breite = doc.internal.pageSize.getWidth() - 2 * rand;
      const hoehe = doc.internal.pageSize.getHeight();
      const grau = [75, 85, 99];
      let y = 18;

      // Neue Seite, wenn der Platz für Überschrift + ein paar Zeilen nicht mehr reicht
      const platz = mm => {
        if (y + mm > hoehe - 16) {
          doc.addPage();
          y = 18;
        }
      };
      const absatz = (text, { groesse = 9, stil = "normal", farbe = [31, 41, 55] } = {}) => {
        doc.setFont("helvetica", stil);
        doc.setFontSize(groesse);
        doc.setTextColor(...farbe);
        const zeilen = doc.splitTextToSize(String(text), breite);
        const zeilenhoehe = groesse * 0.45;
        zeilen.forEach(z => {
          platz(zeilenhoehe);
          doc.text(z, rand, y);
          y += zeilenhoehe;
        });
        y += 1;
      };
      const ueberschrift = text => {
        y += 4;
        platz(18);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(31, 41, 55);
        doc.text(text, rand, y);
        y += 1.5;
        doc.setDrawColor(217, 119, 6);
        doc.setLineWidth(0.6);
        doc.line(rand, y, rand + breite, y);
        y += 4.5;
      };
      const tabelle = optionen => {
        autoTable(doc, {
          startY: y,
          margin: { left: rand, right: rand, top: 18, bottom: 16 },
          theme: "grid",
          styles: { fontSize: 7.5, cellPadding: 1.2, overflow: "linebreak", valign: "top", lineColor: [209, 213, 219], textColor: [31, 41, 55] },
          headStyles: { fillColor: [243, 244, 246], textColor: [31, 41, 55], fontStyle: "bold" },
          rowPageBreak: "avoid",
          ...optionen
        });
        y = doc.lastAutoTable.finalY + 3;
      };

      // Kopf
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(31, 41, 55);
      doc.text(kopf.art, rand, y);
      y += 7;
      absatz(
        `Betroffen: ${kopf.betroffen.bezeichnung} (${kopf.betroffen.typ === "club" ? "Vereinskonto" : "Benutzerkonto"} #${kopf.betroffen.id}) · erstellt am ${erstellt}`,
        { farbe: grau }
      );
      absatz(
        `Verantwortlicher: ${v.name}, ${v.strasse}, ${v.plz} ${v.ort}, ${v.email} (${v.dienst})`,
        { farbe: grau }
      );

      // Pflichtangaben Art. 15 Abs. 1 und 2
      ueberschrift("Informationen zur Verarbeitung");
      tabelle({
        body: [
          ["Zwecke und Rechtsgrundlagen", info.zwecke.map(z => `${z.zweck} (${z.rechtsgrundlage})`).join("; ")],
          ["Kategorien", info.kategorien.join("; ")],
          ["Empfänger", info.empfaenger.join(" ")],
          ["Drittlandsübermittlung", info.drittlandsuebermittlung],
          ["Speicherdauer", info.speicherdauer],
          ["Deine Rechte", `${info.rechte.join("; ")}. ${b.text} ${b.name}, ${b.anschrift}, ${b.web}`],
          ["Herkunft der Daten", info.herkunft],
          ["Automatisierte Entscheidung", `${info.automatisierte_entscheidung} Datenschutzerklärung: ${info.datenschutzerklaerung}`]
        ],
        columnStyles: { 0: { cellWidth: 55, fontStyle: "bold", fillColor: [249, 250, 251] } },
        styles: { fontSize: 8, cellPadding: 1.5, overflow: "linebreak", valign: "top", lineColor: [209, 213, 219], textColor: [31, 41, 55] }
      });

      // Bereiche in der gelieferten Reihenfolge
      daten.bereiche.forEach(bereich => {
        const anzahl = bereich.art === "tabelle" ? ` (${bereich.zeilen.length})` : "";
        ueberschrift(bereich.titel + anzahl);
        if (bereich.hinweis) {
          absatz(bereich.hinweis, { groesse: 8, stil: "italic", farbe: grau });
        }
        switch (bereich.art) {
          case "felder":
            tabelle({
              body: bereich.felder.map(f => [f.label, f.wert]),
              columnStyles: { 0: { cellWidth: breite * 0.25, textColor: grau } }
            });
            break;
          case "tabelle":
            tabelle({
              head: [bereich.spalten],
              body: bereich.zeilen,
              showHead: "everyPage"
            });
            break;
          case "text":
            absatz(bereich.text);
            break;
          default:
            absatz("Keine Daten gespeichert.", { farbe: [107, 114, 128] });
        }
      });

      // Kopf- und Fußzeile auf jeder Seite
      const seiten = doc.internal.getNumberOfPages();
      for (let i = 1; i <= seiten; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128);
        if (i > 1) {
          doc.text(`${kopf.art} – ${kopf.betroffen.bezeichnung} – erstellt am ${erstellt}`, rand, 10);
        }
        doc.text(`Seite ${i} von ${seiten}`, rand + breite, hoehe - 8, { align: "right" });
      }

      return doc;
    }
  }
};
</script>
